import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add as AddIcon,
  Apartment,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  DeleteForeverOutlined,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";

import { useAuth } from "../../context/AuthContext";
import { locationService } from "../../services";
import { RBAC } from "../../services/api";
import CustomModal from "../../Components/CustomModal";
import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const normalizeCompany = (source) => {
  const item = source?.company || source?.branch || source;
  if (!item || typeof item !== "object") return null;

  return {
    id: item.id,
    name: item.name || item.legalName || item.alias || `Sucursal ${item.id}`,
    legalName: item.legalName || item.name || "",
    alias: item.alias || "",
    code: item.code || "",
    tax_id: item.tax_id || item.taxId || "",
    address: item.address || "",
    tax_nit: item.tax_nit || "",
    tax_name: item.tax_name || "",
    tax_address: item.tax_address || "",
    establishment_code: item.establishment_code || "",
    phone: item.phone || "",
    city_id: item.city_id || item.city?.id || "",
    department_id: item.department_id || item.city?.department_id || "",
    city_name: item.city_name || item.city?.name || "",
    department_name: item.department_name || item.city?.department_name || "",
    is_active:
      item.is_active !== undefined
        ? Boolean(item.is_active)
        : item.active !== undefined
          ? Boolean(item.active)
          : true,
    created_at: item.created_at || item.createdAt || null,
    raw: item,
  };
};

const normalizeCity = (city) => ({
  id: city?.id,
  name: city?.name || "Ciudad",
  department_id: city?.department_id || city?.department?.id || "",
  department_name: city?.department_name || "",
});

const normalizeDepartment = (department) => ({
  id: department?.id,
  name: department?.name || "Departamento",
});

const formatDateTime = (value) => {
  if (!value) return "Sin fecha";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin fecha";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
};

const EMPTY_FORM = {
  legalName: "",
  alias: "",
  code: "",
  tax_id: "",
  address: "",
  tax_nit: "",
  tax_name: "",
  tax_address: "",
  establishment_code: "",
  phone: "",
  department_id: "",
  city_id: "",
  is_active: true,
};

function CompanyForm({
  setOpen,
  mode = "create",
  initialValues = EMPTY_FORM,
  departments = [],
  cities = [],
  loadingCities = false,
  onSubmit,
}) {
  const resolveDepartmentId = useCallback(
    (cityId, explicitDepartmentId = "") => {
      if (explicitDepartmentId) return explicitDepartmentId;
      const city = cities.find((item) => String(item.id) === String(cityId));
      return city?.department_id || "";
    },
    [cities]
  );

  const [values, setValues] = useState({
    legalName: initialValues.legalName || "",
    alias: initialValues.alias || "",
    code: initialValues.code || "",
    tax_id: initialValues.tax_id || "",
    address: initialValues.address || "",
    tax_nit: initialValues.tax_nit || "",
    tax_name: initialValues.tax_name || "",
    tax_address: initialValues.tax_address || "",
    establishment_code: initialValues.establishment_code || "",
    phone: initialValues.phone || "",
    department_id: resolveDepartmentId(initialValues.city_id, initialValues.department_id),
    city_id: initialValues.city_id || "",
    is_active: initialValues.is_active ?? true,
  });
  const [loading, setLoading] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  useEffect(() => {
    setValues({
      legalName: initialValues.legalName || "",
      alias: initialValues.alias || "",
      code: initialValues.code || "",
      tax_id: initialValues.tax_id || "",
      address: initialValues.address || "",
      tax_nit: initialValues.tax_nit || "",
      tax_name: initialValues.tax_name || "",
      tax_address: initialValues.tax_address || "",
      establishment_code: initialValues.establishment_code || "",
      phone: initialValues.phone || "",
      department_id: resolveDepartmentId(initialValues.city_id, initialValues.department_id),
      city_id: initialValues.city_id || "",
      is_active: initialValues.is_active ?? true,
    });
  }, [initialValues, resolveDepartmentId]);

  const filteredCities = useMemo(
    () =>
      cities.filter(
        (city) =>
          !values.department_id ||
          String(city.department_id || "") === String(values.department_id)
      ),
    [cities, values.department_id]
  );

  const title = mode === "edit" ? "Guardar cambios" : "Crear sucursal";

  const showSnack = (message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  };

  const handleSave = async () => {
    if (!values.legalName.trim()) {
      showSnack("La razon social es obligatoria.", "warning");
      return;
    }
    if (!values.alias.trim()) {
      showSnack("El alias es obligatorio.", "warning");
      return;
    }
    if (!values.code.trim()) {
      showSnack("El codigo es obligatorio.", "warning");
      return;
    }
    if (!values.tax_id.trim()) {
      showSnack("El NIT es obligatorio.", "warning");
      return;
    }
    if (!values.address.trim()) {
      showSnack("La direccion es obligatoria.", "warning");
      return;
    }

    try {
      setLoading(true);
      await Promise.resolve(
        onSubmit?.({
          legalName: values.legalName.trim(),
          alias: values.alias.trim(),
          code: values.code.trim().toUpperCase(),
          tax_id: values.tax_id.trim().toUpperCase(),
          address: values.address.trim(),
          tax_nit: values.tax_nit.trim().toUpperCase(),
          tax_name: values.tax_name.trim(),
          tax_address: values.tax_address.trim(),
          establishment_code: values.establishment_code.trim(),
          phone: values.phone.trim(),
          city_id: values.city_id || undefined,
          is_active: Boolean(values.is_active),
        })
      );
      showSnack(mode === "edit" ? "Sucursal actualizada." : "Sucursal creada.", "success");
      setTimeout(() => {
        if (typeof setOpen === "function") setOpen(false);
      }, 250);
    } catch (error) {
      showSnack(error?.response?.data?.detail || "No se pudo guardar la sucursal.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Stack spacing={2}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <TextField
            label="Razon social"
            size="small"
            value={values.legalName}
            onChange={(event) =>
              setValues((prev) => ({ ...prev, legalName: event.target.value }))
            }
            required
            autoFocus
            fullWidth
          />
          <TextField
            label="Alias"
            size="small"
            value={values.alias}
            onChange={(event) =>
              setValues((prev) => ({ ...prev, alias: event.target.value }))
            }
            required
            fullWidth
          />
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <TextField
            label="Codigo"
            size="small"
            value={values.code}
            onChange={(event) =>
              setValues((prev) => ({ ...prev, code: event.target.value.toUpperCase() }))
            }
            required
            fullWidth
          />
          <TextField
            label="NIT"
            size="small"
            value={values.tax_id}
            onChange={(event) =>
              setValues((prev) => ({ ...prev, tax_id: event.target.value.toUpperCase() }))
            }
            required
            fullWidth
          />
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <TextField
            label="Telefono"
            size="small"
            value={values.phone}
            onChange={(event) =>
              setValues((prev) => ({ ...prev, phone: event.target.value }))
            }
            fullWidth
          />
          <TextField
            select
            label="Departamento"
            size="small"
            value={values.department_id}
            onChange={(event) =>
              setValues((prev) => ({
                ...prev,
                department_id: event.target.value,
                city_id: "",
              }))
            }
            fullWidth
            disabled={loadingCities}
          >
            <MenuItem value="">
              <em>Sin departamento</em>
            </MenuItem>
            {departments.map((department) => (
              <MenuItem key={department.id} value={department.id}>
                {department.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Municipio / ciudad"
            size="small"
            value={values.city_id}
            onChange={(event) =>
              setValues((prev) => ({ ...prev, city_id: event.target.value }))
            }
            fullWidth
            disabled={loadingCities || !values.department_id}
          >
            <MenuItem value="">
              <em>Sin ciudad</em>
            </MenuItem>
            {filteredCities.map((city) => (
              <MenuItem key={city.id} value={city.id}>
                {city.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <TextField
          label="Direccion"
          size="small"
          value={values.address}
          onChange={(event) =>
            setValues((prev) => ({ ...prev, address: event.target.value }))
          }
          required
          fullWidth
          multiline
          minRows={3}
        />

        <Stack spacing={1.25}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Datos fiscales
          </Typography>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
            <TextField
              label="NIT"
              size="small"
              value={values.tax_nit}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, tax_nit: event.target.value.toUpperCase() }))
              }
              fullWidth
            />
            <TextField
              label="Razón social"
              size="small"
              value={values.tax_name}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, tax_name: event.target.value }))
              }
              fullWidth
            />
          </Stack>
          <TextField
            label="Dirección fiscal"
            size="small"
            value={values.tax_address}
            onChange={(event) =>
              setValues((prev) => ({ ...prev, tax_address: event.target.value }))
            }
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            label="Código establecimiento"
            size="small"
            value={values.establishment_code}
            onChange={(event) =>
              setValues((prev) => ({ ...prev, establishment_code: event.target.value }))
            }
            fullWidth
          />
        </Stack>

        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(values.is_active)}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, is_active: event.target.checked }))
              }
            />
          }
          label="Sucursal activa"
        />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={() => setOpen(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={loading}>
            {loading ? "Guardando..." : title}
          </Button>
        </Stack>
      </Stack>

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </>
  );
}

export default function CompanyAdminPage() {
  const isMobile = useMediaQuery("(max-width:768px)");
  const { user, effectivePerms = [] } = useAuth();

  const isSuperuser = Boolean(user?.is_superuser);
  const permissions = Array.isArray(effectivePerms) ? effectivePerms : [];
  const canManageCompanies = isSuperuser || permissions.includes("manage_companies");

  const [loading, setLoading] = useState(true);
  const [loadingCities, setLoadingCities] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [cities, setCities] = useState([]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, company: null, loading: false });

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = useCallback((message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  }, []);

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      let data = [];
      if (isSuperuser) {
        data = await RBAC.companies.list();
      } else {
        try {
          data = await RBAC.companiesMine();
        } catch {
          data = await RBAC.userCompanies("me");
        }
      }

      const normalized = toArray(data).map(normalizeCompany).filter(Boolean);
      setCompanies(normalized);
    } catch {
      setCompanies([]);
      showSnack("No se pudieron cargar las sucursales.", "error");
    } finally {
      setLoading(false);
    }
  }, [isSuperuser, showSnack]);

  const loadCities = useCallback(async () => {
    setLoadingCities(true);
    try {
      const [departmentsData, citiesData] = await Promise.all([
        locationService.listDepartments({ active: "true" }),
        locationService.listCities({ active: "true" }),
      ]);
      setDepartments(toArray(departmentsData).map(normalizeDepartment).filter(Boolean));
      setCities(toArray(citiesData).map(normalizeCity).filter(Boolean));
    } catch {
      setDepartments([]);
      setCities([]);
      showSnack("No se pudieron cargar departamentos y municipios.", "warning");
    } finally {
      setLoadingCities(false);
    }
  }, [showSnack]);

  useEffect(() => {
    loadCompanies();
    loadCities();
  }, [loadCities, loadCompanies]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return companies.filter((item) => {
      if (statusFilter === "active" && !item.is_active) return false;
      if (statusFilter === "inactive" && item.is_active) return false;

      if (!q) return true;
      const haystack = [
        item.name,
        item.alias,
        item.code,
        item.tax_id,
        item.tax_nit,
        item.tax_name,
        item.establishment_code,
        item.city_name,
        item.department_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [companies, search, statusFilter]);

  const handleCreate = async (payload) => {
    await RBAC.companies.create(payload);
    showSnack("Sucursal creada correctamente.", "success");
    await loadCompanies();
  };

  const handleEdit = async (payload) => {
    if (!selectedCompany?.id) return;
    await RBAC.companies.update(selectedCompany.id, payload);
    showSnack("Sucursal actualizada correctamente.", "success");
    await loadCompanies();
  };

  const handleToggleActive = useCallback(async (row) => {
    if (!row?.id) return;
    try {
      await RBAC.companies.toggleActive(row.id, !row.is_active);
      showSnack(row.is_active ? "Sucursal desactivada." : "Sucursal activada.", "success");
      await loadCompanies();
    } catch (error) {
      showSnack(error?.response?.data?.detail || "No se pudo actualizar el estado.", "error");
    }
  }, [loadCompanies, showSnack]);

  const handleDeleteCompany = useCallback(async () => {
    const company = deleteDialog.company;
    if (!company?.id) return;

    setDeleteDialog((prev) => ({ ...prev, loading: true }));
    try {
      await RBAC.companies.delete(company.id);
      setDeleteDialog({ open: false, company: null, loading: false });
      showSnack("Sucursal eliminada permanentemente.", "success");
      await loadCompanies();
    } catch (error) {
      setDeleteDialog((prev) => ({ ...prev, loading: false }));
      showSnack(
        error?.response?.data?.detail || "No se pudo eliminar permanentemente la sucursal.",
        "error"
      );
    }
  }, [deleteDialog.company, loadCompanies, showSnack]);

const columns = useMemo(
  () => [
    { field: "id", headerName: "ID", width: 90 },
    {
      field: "name",
      headerName: "Sucursal",
      flex: 1.2,
      minWidth: 260,
      renderCell: ({ row }) => {
        const initials = (row.alias || row.name || "SU")
          .split(" ")
          .map((part) => part?.[0] || "")
          .join("")
          .slice(0, 2)
          .toUpperCase();

        return (
          <Stack
            direction="row"
            spacing={1.2}
            alignItems="center"
            sx={{
              height: "100%",
              minWidth: 0,
              py: 0.35,
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: "0.8rem",
                bgcolor: "primary.main",
                flexShrink: 0,
              }}
            >
              {initials}
            </Avatar>

            <Box
              sx={{
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: 0.15,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.15,
                }}
                noWrap
              >
                {row.legalName || row.name}
              </Typography>

              <Stack
                direction="row"
                spacing={0.6}
                alignItems="center"
                sx={{
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1.1 }}
                  noWrap
                >
                  {row.alias || "Sin alias"}
                </Typography>

                <Box
                  sx={{
                    width: 3,
                    height: 3,
                    borderRadius: "50%",
                    bgcolor: "text.disabled",
                    flexShrink: 0,
                  }}
                />

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1.1 }}
                  noWrap
                >
                  {row.code || "Sin codigo"}
                </Typography>
              </Stack>
            </Box>
          </Stack>
        );
      },
    },
    {
      field: "tax_id",
      headerName: "NIT",
      width: 160,
      renderCell: ({ value }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            minWidth: 0,
          }}
        >
          <Typography variant="body2" noWrap sx={{ lineHeight: 1.15 }}>
            {value || "-"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "city_name",
      headerName: "Ciudad",
      minWidth: 190,
      flex: 0.8,
      renderCell: ({ row }) => (
        <Box
          sx={{
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            height: "100%",
            gap: 0.15,
            py: 0.35,
          }}
        >
          <Typography
            variant="body2"
            noWrap
            sx={{
              lineHeight: 1.15,
            }}
          >
            {row.city_name || "Sin ciudad"}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{
              lineHeight: 1.1,
            }}
          >
            {row.department_name || "Sin departamento"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "phone",
      headerName: "Telefono",
      width: 150,
      valueGetter: (_, row) => row?.phone || "-",
      renderCell: ({ value }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            minWidth: 0,
          }}
        >
          <Typography variant="body2" noWrap sx={{ lineHeight: 1.15 }}>
            {value || "-"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "is_active",
      headerName: "Estado",
      width: 120,
      renderCell: ({ value }) => (
        <Chip
          size="small"
          label={value ? "Activa" : "Desactivada"}
          color={value ? "success" : "default"}
          variant={value ? "filled" : "outlined"}
          sx={{
            borderRadius: 999,
            height: 24,
            "& .MuiChip-label": {
              px: 1,
              lineHeight: 1,
            },
          }}
        />
      ),
    },
    {
      field: "created_at",
      headerName: "Creado",
      width: 180,
      renderCell: ({ value }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            minWidth: 0,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              lineHeight: 1.15,
            }}
            noWrap
          >
            {formatDateTime(value)}
          </Typography>
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 170,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.25}>
          <Tooltip title="Editar">
            <span>
              <IconButton
                size="small"
                disabled={!canManageCompanies}
                onClick={() => {
                  setSelectedCompany(row);
                  setEditOpen(true);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={row.is_active ? "Desactivar" : "Activar"}>
            <span>
              <IconButton
                size="small"
                disabled={!canManageCompanies}
                color={row.is_active ? "error" : "success"}
                onClick={() => handleToggleActive(row)}
              >
                {row.is_active ? (
                  <BlockIcon fontSize="small" />
                ) : (
                  <CheckCircleIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>

          {!row.is_active ? (
            <Tooltip title="Eliminar permanentemente">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={!canManageCompanies}
                  onClick={() => setDeleteDialog({ open: true, company: row, loading: false })}
                >
                  <DeleteForeverOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
        </Stack>
      ),
    },
  ],
  [canManageCompanies, handleToggleActive]
);

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <MainText icon={Apartment} title="Sucursales" subtitle="Listado administrativo de sucursales" />

        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Refrescar">
            <IconButton onClick={loadCompanies}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canManageCompanies ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              Crear sucursal
            </Button>
          ) : null}
        </Stack>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Buscar por nombre, alias, codigo, NIT o ciudad..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Estado</InputLabel>
          <Select
            label="Estado"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <MenuItem value="all">Todas</MenuItem>
            <MenuItem value="active">Activas</MenuItem>
            <MenuItem value="inactive">Desactivadas</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Box sx={{ height: isMobile ? "calc(100vh - 280px)" : "calc(100vh - 290px)", width: "100%" }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 20, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
          density={isMobile ? "compact" : "standard"}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#757B2B",
              color: "#ffffff",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover": {
              backgroundColor: "#757B2B !important",
            },
            "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator": {
              color: "#ffffff !important",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(16, 23, 60, 0.08)",
            },
          }}
        />
      </Box>

      <CustomModal
        open={createOpen}
        setOpen={setCreateOpen}
        title="Crear sucursal"
        component={CompanyForm}
        componentProps={{
          mode: "create",
          initialValues: EMPTY_FORM,
          departments,
          cities,
          loadingCities,
          onSubmit: handleCreate,
        }}
        width={isMobile ? "92%" : "760px"}
      />

      <CustomModal
        open={editOpen}
        setOpen={setEditOpen}
        title="Editar sucursal"
        component={CompanyForm}
        componentProps={{
          mode: "edit",
          initialValues: {
            legalName: selectedCompany?.legalName || "",
            alias: selectedCompany?.alias || "",
            code: selectedCompany?.code || "",
            tax_id: selectedCompany?.tax_id || "",
            address: selectedCompany?.address || "",
            tax_nit: selectedCompany?.tax_nit || "",
            tax_name: selectedCompany?.tax_name || "",
            tax_address: selectedCompany?.tax_address || "",
            establishment_code: selectedCompany?.establishment_code || "",
            phone: selectedCompany?.phone || "",
            department_id: selectedCompany?.department_id || "",
            city_id: selectedCompany?.city_id || "",
            is_active: selectedCompany?.is_active ?? true,
          },
          departments,
          cities,
          loadingCities,
          onSubmit: handleEdit,
        }}
        width={isMobile ? "92%" : "760px"}
      />

      <Dialog
        open={deleteDialog.open}
        onClose={() => {
          if (deleteDialog.loading) return;
          setDeleteDialog({ open: false, company: null, loading: false });
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Eliminar sucursal</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Esta accion eliminara permanentemente la sucursal
            {" "}
            <strong>{deleteDialog.company?.legalName || deleteDialog.company?.name || "-"}</strong>.
            Solo se permite porque ya esta desactivada.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, company: null, loading: false })}
            disabled={deleteDialog.loading}
          >
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteDialog.loading || !deleteDialog.company?.id}
            onClick={handleDeleteCompany}
          >
            {deleteDialog.loading ? "Eliminando..." : "Eliminar permanentemente"}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </Box>
  );
}
